import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowUp, FaExclamationCircle, FaEnvelope, FaInfoCircle, FaLock, FaShieldAlt, FaUserShield } from 'react-icons/fa';
import './LegalPages.css';

const PrivacyPolicy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Sections for Table of Contents
  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'information-collected', title: 'Information We Collect' },
    { id: 'information-use', title: 'How We Use Your Information' },
    { id: 'information-sharing', title: 'How We Share Your Information' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'your-rights', title: 'Your Rights and Choices' },
    { id: 'childrens-privacy', title: 'Children\'s Privacy' },
    { id: 'international-transfers', title: 'International Data Transfers' },
    { id: 'changes', title: 'Changes to This Privacy Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="legal-page">
      {/* SVG Definitions for Patterns */}
      <svg 
        width="0" 
        height="0" 
        style={{ position: 'absolute' }}
      >
        <defs>
          <pattern 
            id="gridPattern" 
            width="20" 
            height="20" 
            patternUnits="userSpaceOnUse"
          >
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="white" 
              strokeWidth="0.5" 
              opacity="0.3"
            />
          </pattern>
        </defs>
      </svg>

      <div className="legal-hero">
        <div className="legal-hero__overlay">
          <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect 
              width="100%" 
              height="100%" 
              fill="url(#gridPattern)" 
            />
          </svg>
        </div>
        <div className="legal-container">
          <h1 className="legal-hero__title">Privacy Policy</h1>
          <p className="legal-hero__subtitle">
            How we collect, use, and protect your personal information
          </p>
          <span className="legal-last-updated">
            Last updated: May 4, 2025
          </span>
        </div>
      </div>

      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-toc">
            <h2 className="legal-toc__title">Table of Contents</h2>
            <ul className="legal-toc__list">
              {sections.map(section => (
                <li key={section.id} className="legal-toc__item">
                  <a 
                    href={`#${section.id}`} 
                    className="legal-toc__link"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <section id="introduction" className="legal-section">
            <h2 className="legal-section__title">1. Introduction</h2>
            <p>
              At CVance & OppLiy ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, or any of our services (collectively, the "Services").
            </p>
            <p>
              Please read this Privacy Policy carefully. By using our Services, you consent to the data practices described in this policy.
            </p>
            <div className="legal-highlight legal-highlight--info">
              <p>
                <FaInfoCircle style={{ marginRight: '10px' }} />
                CVance and OppLiy are currently in the project development phase. We are not a registered company or LLC at this time. This is a non-profit project aimed at addressing challenges in the job search process.
              </p>
            </div>
          </section>

          <section id="information-collected" className="legal-section">
            <h2 className="legal-section__title">2. Information We Collect</h2>
            <p>
              We collect several types of information from and about users of our Services:
            </p>
            <h3 className="legal-section__subtitle">Personal Information</h3>
            <p>
              When you register for an account or use our Services, we may collect:
            </p>
            <ul>
              <li>Contact information (name, email address, phone number)</li>
              <li>Account credentials (username, password)</li>
              <li>Professional information (education history, work experience, skills, projects, certifications)</li>
              <li>Resume content and documents you upload</li>
              <li>Job descriptions you enter</li>
              <li>Payment information (processed by our third-party payment processors)</li>
            </ul>

            <h3 className="legal-section__subtitle">Usage Information</h3>
            <p>
              We automatically collect certain information about your device and how you interact with our Services, including:
            </p>
            <ul>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages visited, features used, time spent on the Services)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
            <div className="legal-highlight">
              <p>
                <FaShieldAlt style={{ marginRight: '10px', color: '#4a6bfd' }} />
                We collect only the information necessary to provide you with our Services and improve your experience.
              </p>
            </div>
          </section>

          <section id="information-use" className="legal-section">
            <h2 className="legal-section__title">3. How We Use Your Information</h2>
            <p>
              We use the information we collect for various purposes, including to:
            </p>
            <ul>
              <li>Provide, maintain, and improve our Services</li>
              <li>Create and tailor resumes based on your profile and job descriptions</li>
              <li>Process your subscriptions and payments</li>
              <li>Communicate with you about your account, our Services, and promotional offers</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Monitor and analyze trends, usage, and activities in connection with our Services</li>
              <li>Detect, prevent, and address technical issues, fraud, or illegal activity</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section id="information-sharing" className="legal-section">
            <h2 className="legal-section__title">4. How We Share Your Information</h2>
            <p>
              We may share your information in the following circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> We may share your information with third-party vendors, service providers, and contractors who perform services on our behalf, such as hosting, data analysis, payment processing, customer service, and marketing.
              </li>
              <li>
                <strong>Business Transfers:</strong> If we are involved in a merger, acquisition, financing, or sale of assets, your information may be transferred as part of that transaction.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, government requests).
              </li>
              <li>
                <strong>Protection of Rights:</strong> We may share your information to protect our rights, privacy, safety, or property, or that of our users or others.
              </li>
            </ul>
            <p>
              We do not sell or rent your personal information to third parties for their marketing purposes without your explicit consent.
            </p>
            <div className="legal-highlight">
              <p>
                <FaUserShield style={{ marginRight: '10px', color: '#4a6bfd' }} />
                Your privacy is our priority. We only share your information when necessary to provide our services or when required by law.
              </p>
            </div>
          </section>

          <section id="data-retention" className="legal-section">
            <h2 className="legal-section__title">5. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your personal information, we will securely delete or anonymize it.
            </p>
          </section>

          <section id="data-security" className="legal-section">
            <h2 className="legal-section__title">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, use, disclosure, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
            <div className="legal-highlight">
              <p>
                <FaLock style={{ marginRight: '10px', color: '#4a6bfd' }} />
                We use industry-standard security measures to protect your data, but no system is completely secure. We continuously work to improve our security practices.
              </p>
            </div>
          </section>

          <section id="your-rights" className="legal-section">
            <h2 className="legal-section__title">7. Your Rights and Choices</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul>
              <li>Access to your personal information</li>
              <li>Correction of inaccurate or incomplete information</li>
              <li>Deletion of your personal information</li>
              <li>Restriction or objection to certain processing activities</li>
              <li>Data portability</li>
              <li>Withdrawal of consent</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided in the "Contact Us" section.
            </p>
            <h3 className="legal-section__subtitle">Account Information</h3>
            <p>
              You can review and update your account information at any time by logging into your account and accessing your profile settings.
            </p>
            <h3 className="legal-section__subtitle">Communications Preferences</h3>
            <p>
              You can opt out of receiving promotional emails from us by following the unsubscribe instructions in those emails. Even if you opt out, we may still send you non-promotional communications, such as those about your account or our ongoing business relations.
            </p>
            <h3 className="legal-section__subtitle">Cookies</h3>
            <p>
              Most web browsers are set to accept cookies by default. You can adjust your browser settings to remove or reject browser cookies. Please note that removing or rejecting cookies could affect the availability and functionality of our Services.
            </p>
          </section>

          <section id="childrens-privacy" className="legal-section">
            <h2 className="legal-section__title">8. Children's Privacy</h2>
            <p>
              Our Services are not intended for individuals under the age of 16. We do not knowingly collect personal information from children under 16. If we learn we have collected personal information from a child under 16, we will delete that information promptly.
            </p>
            <div className="legal-highlight legal-highlight--warning">
              <p>
                <FaExclamationCircle style={{ marginRight: '10px', color: '#f59e0b' }} />
                If you believe we might have any information from or about a child under 16, please contact us immediately.
              </p>
            </div>
          </section>

          <section id="international-transfers" className="legal-section">
            <h2 className="legal-section__title">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction. If you are located outside the United States and choose to provide information to us, please note that we transfer the information, including personal information, to the United States and process it there.
            </p>
            <p>
              When we transfer personal information from the European Economic Area (EEA), United Kingdom, or Switzerland to countries that have not been deemed to provide an adequate level of protection, we use specific legal mechanisms, such as Standard Contractual Clauses, to protect your information.
            </p>
          </section>

          <section id="changes" className="legal-section">
            <h2 className="legal-section__title">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes to our information practices. We will notify you of any material changes by posting the updated Privacy Policy on our website and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for the latest information on our privacy practices.
            </p>
          </section>

          <section id="contact" className="legal-section">
            <h2 className="legal-section__title">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p>
              Email: <a href="mailto:oppliycvance@gmail.com" style={{ color: '#4a6bfd', fontWeight: '600' }}>oppliycvance@gmail.com</a>
            </p>
          </section>
        </div>

        <div className="legal-contact">
          <h2 className="legal-contact__title">Privacy Concerns?</h2>
          <p className="legal-contact__text">
            We take your privacy seriously. If you have any questions, concerns, or requests regarding your personal information, our support team is ready to assist you.
          </p>
          <Link to="/contact" className="legal-contact__button">
            Contact Support
          </Link>
        </div>
      </div>

      {/* Back to top button */}
      <div 
        className={`legal-back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <FaArrowUp />
      </div>
    </div>
  );
};

export default PrivacyPolicy;